import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar, { ASSETS } from '../components/Navbar';
import { 
  Users, 
  Calendar, 
  Heart, 
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Eye,
  Sparkles,
  BarChart3,
  BarChart2,
  Loader2,
  X,
  AlertTriangle,
  AlertCircle,
  ThumbsUp,
  MessageSquare,
  Activity,
  TrendingUp,
  ChevronRight,
  User,
  UserPlus,
  MapPin,
  Briefcase,
  Target,
  Brain,
  Shield,
  List,
  ArrowLeft,
  Search,
  Filter
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    city: '',
    venue_name: '',
    date: '',
    event_type: 'dinner',
    max_capacity: 7
  });

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, appsRes, eventsRes, usersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/applications/all`, { withCredentials: true }),
        axios.get(`${API}/admin/events`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/approvals/pending`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
      setPendingReviews(pendingRes.data);
      
      // Load audit logs
      try {
        const logsRes = await axios.get(`${API}/admin/audit-logs`, { withCredentials: true });
        setAuditLogs(logsRes.data);
      } catch (e) {
        console.log('Audit logs not available');
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewUser = async (userId, status, note = '') => {
    setActionLoading(userId);
    try {
      const endpoint = status === 'approved' ? 'approve' 
        : status === 'waitlisted' ? 'waitlist' 
        : 'reject';
      
      await axios.post(
        `${API}/admin/approvals/${userId}/${endpoint}`,
        { note },
        { withCredentials: true }
      );
      fetchData();
      setSelectedUser(null);
      setNoteText('');
    } catch (error) {
      console.error('Error reviewing user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddNote = async (entityType, entityId) => {
    if (!noteText.trim()) return;
    try {
      await axios.post(
        `${API}/admin/notes`,
        { entity_type: entityType, entity_id: entityId, note: noteText },
        { withCredentials: true }
      );
      setNoteText('');
      fetchData();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const response = await axios.get(
        `${API}/admin/users/${userId}/detail`,
        { withCredentials: true }
      );
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Error loading user detail:', error);
    }
  };

  const handleCreateEvent = async () => {
    try {
      await axios.post(`${API}/admin/events`, newEvent, { withCredentials: true });
      setShowCreateEvent(false);
      setNewEvent({
        title: '',
        description: '',
        city: '',
        venue_name: '',
        date: '',
        event_type: 'dinner',
        max_capacity: 7
      });
      fetchData();
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  // Access denied
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-serif text-[#F5F0E6] mb-2">{t('admin_access_denied')}</h1>
            <p className="text-[#8A8A8A]">{t('admin_no_permission')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <img src={ASSETS.logo} alt="Loading..." className="w-20 h-20 logo-glow animate-pulse object-contain" />
        </div>
      </div>
    );
  }

  // Score indicator component
  const ScoreIndicator = ({ score, label }) => (
    <div className="flex items-center gap-2">
      <div className="w-full bg-[#8B1A1A]/20 h-2 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all ${
            score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-xs text-[#8A8A8A] w-12">{score?.toFixed(0)}%</span>
    </div>
  );

  // AI Recommendation badge
  const AIBadge = ({ recommendation, confidence }) => {
    const colors = {
      approve: 'bg-emerald-900/30 text-emerald-400 border-emerald-500/30',
      review: 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30',
      waitlist: 'bg-orange-900/30 text-orange-400 border-orange-500/30',
      reject: 'bg-red-900/30 text-red-400 border-red-500/30',
      pending: 'bg-[#8B1A1A]/20 text-[#8B1A1A] border-[#8B1A1A]/30'
    };
    
    return (
      <span className={`px-2 py-1 text-xs border flex items-center gap-1 ${colors[recommendation] || colors.pending}`}>
        <Brain size={12} />
        {recommendation} ({confidence?.toFixed(0)}%)
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="label-premium mb-3">{t('admin_title')}</p>
          <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F0E6]">
            Soul Mate <span className="text-[#8B1A1A]">Admin</span>
          </h1>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {[
            { label: t('admin_users'), value: stats?.total_users || 0, icon: Users },
            { label: t('admin_pending'), value: stats?.pending_review || stats?.by_status?.under_review || 0, icon: Clock },
            { label: t('admin_approved'), value: stats?.approved_users || 0, icon: CheckCircle },
            { label: t('admin_events'), value: stats?.total_events || 0, icon: Calendar },
            { label: t('admin_matches'), value: stats?.total_matches || 0, icon: Heart }
          ].map((stat, i) => (
            <div key={i} className="card-premium p-6" data-testid={`stat-${stat.label}`}>
              <stat.icon className="text-[#8B1A1A] mb-3" size={24} />
              <p className="text-3xl font-serif text-[#8B1A1A]">{stat.value}</p>
              <p className="text-[#8A8A8A] text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Conversion Funnel */}
        {stats?.conversion && (
          <div className="card-premium p-6 mb-8">
            <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-2">
              <TrendingUp size={20} className="text-[#8B1A1A]" />
              Funnel de Conversión
            </h3>
            <div className="flex items-center justify-between gap-2 text-sm">
              {[
                { label: 'Registrados', value: stats.conversion.registered },
                { label: 'Iniciaron', value: stats.conversion.started_questionnaire },
                { label: 'Completaron', value: stats.conversion.completed_questionnaire },
                { label: 'Aprobados', value: stats.conversion.approved }
              ].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-serif text-[#F5F0E6]">{step.value}</p>
                    <p className="text-[#8A8A8A] text-xs">{step.label}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <ChevronRight className="text-[#8B1A1A]/30" size={20} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-6 mb-10 border-b border-[#8B1A1A]/10 overflow-x-auto">
          {[
            { id: 'overview', label: t('admin_overview'), icon: BarChart3 },
            { id: 'review', label: 'Cola de Revisión', icon: List },
            { id: 'applications', label: t('admin_applications'), icon: FileText },
            { id: 'events', label: t('admin_events'), icon: Calendar },
            { id: 'matches_admin', label: 'Matches', icon: Heart },
            { id: 'explainable_ai', label: 'IA Explicable', icon: Brain },
            { id: 'alerts', label: 'Alertas', icon: AlertCircle },
            { id: 'pilot', label: 'Piloto', icon: Target },
            { id: 'users', label: t('admin_users'), icon: Users },
            { id: 'logs', label: 'Audit Logs', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 px-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#8B1A1A] text-[#8B1A1A]'
                  : 'border-transparent text-[#8A8A8A] hover:text-[#F5F0E6]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        
        {/* REVIEW QUEUE TAB - Cola de Revisión */}
        {activeTab === 'review' && (
          <div className="space-y-4">
            <h2 className="text-2xl font-serif text-[#F5F0E6] mb-6">
              Cola de Revisión ({pendingReviews.length} pendientes)
            </h2>
            
            {pendingReviews.map(item => {
              const u = item.user || {};
              const profile = item.derived_profile || {};
              const quest = item.questionnaire || {};
              
              return (
                <div key={u.user_id} className="card-premium p-6" data-testid={`review-${u.user_id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-xl font-serif text-[#F5F0E6]">
                          {quest.basic_info?.name || u.name}
                        </h3>
                        {profile.ai_recommendation && (
                          <AIBadge 
                            recommendation={profile.ai_recommendation} 
                            confidence={profile.ai_confidence}
                          />
                        )}
                      </div>
                      
                      {/* Basic Info */}
                      <div className="flex items-center gap-4 text-sm text-[#8A8A8A] mb-4 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {quest.basic_info?.age || u.age} años
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {quest.basic_info?.city || u.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase size={14} />
                          {quest.basic_info?.occupation || u.occupation}
                        </span>
                        <span className="flex items-center gap-1">
                          <Target size={14} />
                          {quest.intention?.looking_for}
                        </span>
                      </div>
                      
                      {/* Scores */}
                      {profile && (
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-[#8A8A8A] mb-1">Intención Formal</p>
                            <ScoreIndicator score={profile.formal_intent_score} />
                          </div>
                          <div>
                            <p className="text-xs text-[#8A8A8A] mb-1">Afinidad Premium</p>
                            <ScoreIndicator score={profile.premium_affinity_score} />
                          </div>
                          <div>
                            <p className="text-xs text-[#8A8A8A] mb-1">Disposición Eventos</p>
                            <ScoreIndicator score={profile.event_readiness_score} />
                          </div>
                        </div>
                      )}
                      
                      {/* Strengths & Red Flags */}
                      <div className="flex gap-4 flex-wrap">
                        {profile.strengths?.length > 0 && (
                          <div className="flex items-start gap-1">
                            <ThumbsUp size={14} className="text-emerald-500 mt-0.5" />
                            <div className="text-xs text-emerald-400">
                              {profile.strengths.slice(0, 2).join(' • ')}
                            </div>
                          </div>
                        )}
                        {profile.red_flags?.length > 0 && (
                          <div className="flex items-start gap-1">
                            <AlertTriangle size={14} className="text-yellow-500 mt-0.5" />
                            <div className="text-xs text-yellow-400">
                              {profile.red_flags.slice(0, 2).join(' • ')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => loadUserDetail(u.user_id)}
                        className="btn-outline-gold text-sm flex items-center gap-2"
                        data-testid={`view-detail-${u.user_id}`}
                      >
                        <Eye size={16} />
                        Ver Detalle
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReviewUser(u.user_id, 'approved')}
                          disabled={actionLoading === u.user_id}
                          className="p-2 border border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-500 transition-colors"
                          data-testid={`approve-${u.user_id}`}
                        >
                          {actionLoading === u.user_id ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <CheckCircle size={18} />
                          )}
                        </button>
                        <button
                          onClick={() => handleReviewUser(u.user_id, 'waitlisted')}
                          disabled={actionLoading === u.user_id}
                          className="p-2 border border-yellow-500/50 hover:bg-yellow-500/10 text-yellow-500 transition-colors"
                          data-testid={`waitlist-${u.user_id}`}
                        >
                          <Clock size={18} />
                        </button>
                        <button
                          onClick={() => handleReviewUser(u.user_id, 'rejected')}
                          disabled={actionLoading === u.user_id}
                          className="p-2 border border-red-500/50 hover:bg-red-500/10 text-red-500 transition-colors"
                          data-testid={`reject-${u.user_id}`}
                        >
                          <XCircle size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {pendingReviews.length === 0 && (
              <div className="card-premium p-12 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <p className="text-[#F5F0E6] font-serif text-lg mb-2">¡Todo al día!</p>
                <p className="text-[#8A8A8A]">No hay usuarios pendientes de revisión</p>
              </div>
            )}
          </div>
        )}

        {/* APPLICATIONS TAB */}
        {activeTab === 'applications' && (
          <div className="space-y-4">
            {applications.map(app => (
              <div key={app.application_id || app.user_id} className="card-premium p-6" data-testid={`application-${app.application_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl font-serif text-[#F5F0E6]">{app.name}</h3>
                      <span className={`px-3 py-1 text-xs ${
                        app.status === 'approved' ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-500/30' :
                        app.status === 'pending' ? 'bg-[#8B1A1A]/10 text-[#8B1A1A] border border-[#8B1A1A]/30' :
                        app.status === 'waitlisted' ? 'bg-amber-900/30 text-amber-400 border border-amber-500/30' :
                        'bg-red-900/30 text-red-400 border border-red-500/30'
                      }`}>
                        {app.status}
                      </span>
                      {app.ai_recommendation && (
                        <AIBadge recommendation={app.ai_recommendation} confidence={app.ai_confidence} />
                      )}
                    </div>
                    <p className="text-[#8A8A8A] text-sm">
                      {app.age} años • {app.city} • {app.occupation}
                    </p>
                    {app.strengths?.length > 0 && (
                      <p className="text-emerald-400 text-xs mt-2">
                        <ThumbsUp size={12} className="inline mr-1" />
                        {app.strengths.join(' • ')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadUserDetail(app.user_id)}
                      className="p-2 border border-[#8B1A1A]/20 hover:border-[#8B1A1A] transition-colors"
                      data-testid={`view-app-${app.application_id}`}
                    >
                      <Eye size={18} className="text-[#8A8A8A]" />
                    </button>
                    {app.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleReviewUser(app.user_id, 'approved')}
                          className="p-2 border border-emerald-500/50 hover:bg-emerald-500/10 transition-colors"
                          data-testid={`approve-${app.application_id}`}
                        >
                          <CheckCircle size={18} className="text-emerald-500" />
                        </button>
                        <button
                          onClick={() => handleReviewUser(app.user_id, 'rejected')}
                          className="p-2 border border-red-500/50 hover:bg-red-500/10 transition-colors"
                          data-testid={`reject-${app.application_id}`}
                        >
                          <XCircle size={18} className="text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {applications.length === 0 && (
              <div className="card-premium p-12 text-center">
                <FileText className="w-12 h-12 text-[#8B1A1A] mx-auto mb-4 opacity-50" />
                <p className="text-[#8A8A8A]">{t('admin_no_apps')}</p>
              </div>
            )}
          </div>
        )}

        {/* EVENTS TAB */}
        {activeTab === 'events' && (
          <EventsManagementTab 
            events={events}
            onCreateEvent={() => setShowCreateEvent(true)}
            onRefresh={fetchData}
            t={t}
          />
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {users.map(u => (
              <div key={u.user_id} className="card-premium p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full border border-[#8B1A1A]/30" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#161616] border border-[#8B1A1A]/30 flex items-center justify-center">
                      <span className="text-[#8B1A1A]">{u.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-[#F5F0E6] font-medium">{u.name}</h3>
                    <p className="text-[#8A8A8A] text-sm">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-xs ${
                    u.status === 'approved' ? 'bg-emerald-900/30 text-emerald-400' :
                    u.status === 'under_review' ? 'bg-[#8B1A1A]/10 text-[#8B1A1A]' :
                    'bg-[#8A8A8A]/20 text-[#8A8A8A]'
                  }`}>
                    {u.status}
                  </span>
                  <span className={`px-3 py-1 text-xs ${
                    u.role === 'admin' || u.role === 'super_admin' 
                      ? 'bg-purple-900/30 text-purple-400' 
                      : 'bg-[#161616] text-[#8A8A8A]'
                  }`}>
                    {u.role}
                  </span>
                  <button
                    onClick={() => loadUserDetail(u.user_id)}
                    className="p-2 border border-[#8B1A1A]/20 hover:border-[#8B1A1A]"
                  >
                    <Eye size={16} className="text-[#8A8A8A]" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card-premium p-8">
              <h3 className="text-xl font-serif text-[#F5F0E6] mb-6">{t('admin_pending_apps')}</h3>
              <div className="space-y-4">
                {pendingReviews.slice(0, 5).map(item => {
                  const u = item.user || {};
                  const profile = item.derived_profile || {};
                  return (
                    <div key={u.user_id} className="flex items-center justify-between py-3 border-b border-[#8B1A1A]/10 last:border-0">
                      <div>
                        <p className="text-[#F5F0E6]">{u.name}</p>
                        <p className="text-[#8A8A8A] text-sm">{u.city}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {profile.ai_recommendation && (
                          <span className={`text-xs px-2 py-0.5 ${
                            profile.ai_recommendation === 'approve' ? 'text-emerald-400' :
                            profile.ai_recommendation === 'review' ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {profile.ai_recommendation}
                          </span>
                        )}
                        <button
                          onClick={() => handleReviewUser(u.user_id, 'approved')}
                          className="p-1.5 hover:bg-emerald-500/10 transition-colors"
                        >
                          <CheckCircle size={18} className="text-emerald-500" />
                        </button>
                        <button
                          onClick={() => handleReviewUser(u.user_id, 'rejected')}
                          className="p-1.5 hover:bg-red-500/10 transition-colors"
                        >
                          <XCircle size={18} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {pendingReviews.length === 0 && (
                  <p className="text-[#8A8A8A] text-center py-8">{t('admin_no_pending')}</p>
                )}
              </div>
            </div>

            <div className="card-premium p-8">
              <h3 className="text-xl font-serif text-[#F5F0E6] mb-6">{t('admin_recent_events')}</h3>
              <div className="space-y-4">
                {events.slice(0, 5).map(event => (
                  <div key={event.event_id} className="py-3 border-b border-[#8B1A1A]/10 last:border-0">
                    <div className="flex items-center justify-between">
                      <p className="text-[#F5F0E6]">{event.title}</p>
                      <span className="text-[#8A8A8A] text-sm">
                        {event.current_capacity || 0}/{event.max_capacity}
                      </span>
                    </div>
                    <p className="text-[#8A8A8A] text-sm">
                      {new Date(event.date).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="text-[#8A8A8A] text-center py-8">{t('admin_no_events')}</p>
                )}
              </div>
            </div>
          </div>
        )}


        {/* MATCHES ANALYTICS TAB */}
        {activeTab === 'matches_admin' && (
          <AdminMatchAnalytics />
        )}

        {/* EXPLAINABLE AI TAB */}
        {activeTab === 'explainable_ai' && (
          <ExplainableAIDashboard />
        )}

        {/* ALERTS TAB */}
        {activeTab === 'alerts' && (
          <AdminAlertsDashboard />
        )}

        {/* PILOT TAB */}
        {activeTab === 'pilot' && (
          <PilotDashboard />
        )}

        {/* AUDIT LOGS TAB */}
        {activeTab === 'logs' && (
          <div className="space-y-2">
            {auditLogs.slice(0, 50).map((log, i) => (
              <div key={log.log_id || i} className="card-premium p-4 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <Activity size={16} className="text-[#8B1A1A]" />
                  <span className="text-[#F5F0E6]">{log.action}</span>
                  <span className="text-[#8A8A8A]">{log.entity_type}/{log.entity_id?.slice(0, 12)}</span>
                </div>
                <div className="text-[#8A8A8A] text-xs">
                  {new Date(log.created_at).toLocaleString('es-ES')}
                </div>
              </div>
            ))}
            {auditLogs.length === 0 && (
              <div className="card-premium p-12 text-center">
                <Activity className="w-12 h-12 text-[#8B1A1A] mx-auto mb-4 opacity-50" />
                <p className="text-[#8A8A8A]">No hay logs de auditoría</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-6">
          <div className="bg-[#111111] border border-[#8B1A1A]/20 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#111111] border-b border-[#8B1A1A]/10 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-serif text-[#F5F0E6]">
                  {selectedUser.questionnaire?.basic_info?.name || selectedUser.user?.name}
                </h2>
                <p className="text-[#8A8A8A] text-sm">{selectedUser.user?.email}</p>
              </div>
              <button 
                onClick={() => setSelectedUser(null)} 
                className="text-[#8A8A8A] hover:text-[#F5F0E6] p-2"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-8 space-y-8">
              {/* AI Analysis Section */}
              {selectedUser.derived_profile && (
                <div className="bg-[#8B1A1A]/5 border border-[#8B1A1A]/20 p-6">
                  <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-2">
                    <Brain className="text-[#8B1A1A]" />
                    Análisis IA
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-[#8A8A8A] mb-1">Intención Formal</p>
                      <ScoreIndicator score={selectedUser.derived_profile.formal_intent_score} />
                    </div>
                    <div>
                      <p className="text-xs text-[#8A8A8A] mb-1">Afinidad Premium</p>
                      <ScoreIndicator score={selectedUser.derived_profile.premium_affinity_score} />
                    </div>
                    <div>
                      <p className="text-xs text-[#8A8A8A] mb-1">Disposición Eventos</p>
                      <ScoreIndicator score={selectedUser.derived_profile.event_readiness_score} />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <AIBadge 
                      recommendation={selectedUser.derived_profile.ai_recommendation}
                      confidence={selectedUser.derived_profile.ai_confidence}
                    />
                    <p className="text-[#8A8A8A] text-sm">{selectedUser.derived_profile.ai_explanation}</p>
                  </div>
                  
                  <p className="text-[#F5F0E6] text-sm italic mb-4">
                    "{selectedUser.derived_profile.profile_summary}"
                  </p>
                  
                  {selectedUser.derived_profile.strengths?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-emerald-400 text-xs mb-1 flex items-center gap-1">
                        <ThumbsUp size={12} /> Fortalezas
                      </p>
                      <ul className="text-[#8A8A8A] text-sm list-disc list-inside">
                        {selectedUser.derived_profile.strengths.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedUser.derived_profile.red_flags?.length > 0 && (
                    <div>
                      <p className="text-yellow-400 text-xs mb-1 flex items-center gap-1">
                        <AlertTriangle size={12} /> Alertas
                      </p>
                      <ul className="text-[#8A8A8A] text-sm list-disc list-inside">
                        {selectedUser.derived_profile.red_flags.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Questionnaire Data */}
              {selectedUser.questionnaire && (
                <>
                  <div>
                    <p className="label-premium mb-3">Datos Básicos</p>
                    <p className="text-[#F5F0E6]">
                      {selectedUser.questionnaire.basic_info?.name}, {selectedUser.questionnaire.basic_info?.age} años
                    </p>
                    <p className="text-[#8A8A8A]">
                      {selectedUser.questionnaire.basic_info?.city} • {selectedUser.questionnaire.basic_info?.occupation}
                    </p>
                    {selectedUser.questionnaire.basic_info?.instagram && (
                      <p className="text-[#8A8A8A]">IG: {selectedUser.questionnaire.basic_info?.instagram}</p>
                    )}
                  </div>
                  
                  <div>
                    <p className="label-premium mb-3">Estilo de Vida</p>
                    <p className="text-[#8A8A8A]">
                      {selectedUser.questionnaire.lifestyle?.lifestyle_type} • 
                      Valora: {selectedUser.questionnaire.lifestyle?.free_time_value}
                    </p>
                  </div>
                  
                  <div>
                    <p className="label-premium mb-3">Intención</p>
                    <p className="text-[#8A8A8A]">{selectedUser.questionnaire.intention?.looking_for}</p>
                  </div>
                  
                  <div>
                    <p className="label-premium mb-3">Personalidad</p>
                    <p className="text-[#8A8A8A]">{selectedUser.questionnaire.personality?.personality_description}</p>
                    <p className="text-[#8A8A8A] mt-1">Estilo en citas: {selectedUser.questionnaire.personality?.date_style}</p>
                  </div>
                  
                  <div>
                    <p className="label-premium mb-3">Conexión</p>
                    <p className="text-[#8A8A8A]">
                      Atraído por: {selectedUser.questionnaire.connection?.attraction_factors?.join(', ')}
                    </p>
                    <p className="text-[#8A8A8A] mt-2">
                      No tolera: {selectedUser.questionnaire.connection?.deal_breakers}
                    </p>
                  </div>
                  
                  <div>
                    <p className="label-premium mb-3">Match Inteligente</p>
                    <p className="text-[#8A8A8A]">Ideal: {selectedUser.questionnaire.intelligent_match?.ideal_person}</p>
                    <p className="text-[#8A8A8A] mt-2">
                      Incompatible: {selectedUser.questionnaire.intelligent_match?.incompatible_energy}
                    </p>
                  </div>
                  
                  <div>
                    <p className="label-premium mb-3">¿Por qué Soul Mate Dates?</p>
                    <p className="text-[#8A8A8A]">{selectedUser.questionnaire.exclusivity?.why_join}</p>
                  </div>
                </>
              )}
              
              {/* Admin Notes */}
              <div>
                <p className="label-premium mb-3 flex items-center gap-2">
                  <MessageSquare size={16} />
                  Notas de Admin
                </p>
                
                {selectedUser.admin_notes?.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {selectedUser.admin_notes.map((note, i) => (
                      <div key={i} className="bg-[#0B0B0B] p-3 border border-[#8B1A1A]/10">
                        <p className="text-[#8A8A8A] text-sm">{note.note}</p>
                        <p className="text-[#8A8A8A]/50 text-xs mt-1">
                          {new Date(note.created_at).toLocaleString('es-ES')}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#8A8A8A] text-sm mb-4">Sin notas</p>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Agregar nota..."
                    className="input-premium flex-1"
                  />
                  <button
                    onClick={() => handleAddNote('user', selectedUser.user?.user_id)}
                    className="btn-outline-gold"
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            {selectedUser.user?.status === 'under_review' && (
              <div className="sticky bottom-0 bg-[#111111] border-t border-[#8B1A1A]/10 p-6 flex gap-4">
                <button
                  onClick={() => handleReviewUser(selectedUser.user?.user_id, 'approved', noteText)}
                  disabled={actionLoading}
                  className="btn-gold flex-1"
                >
                  {actionLoading ? <Loader2 className="animate-spin mx-auto" /> : 'Aprobar'}
                </button>
                <button
                  onClick={() => handleReviewUser(selectedUser.user?.user_id, 'waitlisted', noteText)}
                  disabled={actionLoading}
                  className="btn-outline-gold flex-1"
                >
                  Lista de Espera
                </button>
                <button
                  onClick={() => handleReviewUser(selectedUser.user?.user_id, 'rejected', noteText)}
                  disabled={actionLoading}
                  className="flex-1 border border-red-500/50 text-red-400 px-6 py-3 hover:bg-red-500/10 transition-colors"
                >
                  Rechazar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateEvent && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-6">
          <div className="bg-[#111111] border border-[#8B1A1A]/20 max-w-lg w-full">
            <div className="border-b border-[#8B1A1A]/10 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-serif text-[#F5F0E6]">Crear Evento</h2>
              <button onClick={() => setShowCreateEvent(false)} className="text-[#8A8A8A] hover:text-[#F5F0E6]">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="label-premium block mb-2">Título</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  className="input-premium"
                  placeholder="Nombre del evento"
                />
              </div>
              
              <div>
                <label className="label-premium block mb-2">Descripción</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  className="input-premium h-24 resize-none"
                  placeholder="Descripción del evento"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-premium block mb-2">Ciudad</label>
                  <input
                    type="text"
                    value={newEvent.city}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, city: e.target.value }))}
                    className="input-premium"
                    placeholder="Ciudad"
                  />
                </div>
                <div>
                  <label className="label-premium block mb-2">Venue</label>
                  <input
                    type="text"
                    value={newEvent.venue_name}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, venue_name: e.target.value }))}
                    className="input-premium"
                    placeholder="Nombre del lugar"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-premium block mb-2">Fecha</label>
                  <input
                    type="datetime-local"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                    className="input-premium"
                  />
                </div>
                <div>
                  <label className="label-premium block mb-2">Capacidad</label>
                  <input
                    type="number"
                    value={newEvent.max_capacity}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, max_capacity: parseInt(e.target.value) }))}
                    className="input-premium"
                    min={5}
                    max={40}
                  />
                </div>
              </div>
              
              <div>
                <label className="label-premium block mb-2">Tipo de Evento</label>
                <select
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, event_type: e.target.value }))}
                  className="input-premium"
                >
                  <option value="dinner">Cena</option>
                  <option value="wine">Cata de Vinos</option>
                  <option value="cocktails">Cócteles</option>
                  <option value="experience">Experiencia</option>
                </select>
              </div>
            </div>
            
            <div className="border-t border-[#8B1A1A]/10 p-6 flex gap-4">
              <button onClick={() => setShowCreateEvent(false)} className="btn-outline-gold flex-1">
                Cancelar
              </button>
              <button onClick={handleCreateEvent} className="btn-gold flex-1">
                Crear Evento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== EVENTS MANAGEMENT TAB ==============
const EventsManagementTab = ({ events, onCreateEvent, onRefresh, t }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [suggestedParticipants, setSuggestedParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeEventTab, setActiveEventTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(null);

  const loadEventDetail = async (eventId) => {
    setLoading(true);
    try {
      const [detailRes, suggestedRes] = await Promise.all([
        axios.get(`${API}/admin/events/${eventId}`, { withCredentials: true }),
        axios.get(`${API}/admin/events/${eventId}/suggested-participants`, { withCredentials: true })
      ]);
      setEventDetail(detailRes.data);
      setSuggestedParticipants(suggestedRes.data.candidates || []);
      setSelectedEvent(eventId);
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(
        `${API}/admin/events/${selectedEvent}/invite`,
        { user_ids: [userId] },
        { withCredentials: true }
      );
      await loadEventDetail(selectedEvent);
    } catch (error) {
      console.error('Error inviting user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveInvitation = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.delete(
        `${API}/admin/events/${selectedEvent}/invitations/${userId}`,
        { withCredentials: true }
      );
      await loadEventDetail(selectedEvent);
    } catch (error) {
      console.error('Error removing invitation:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEventAction = async (action) => {
    setActionLoading(action);
    try {
      await axios.post(
        `${API}/admin/events/${selectedEvent}/${action}`,
        {},
        { withCredentials: true }
      );
      await loadEventDetail(selectedEvent);
      onRefresh();
    } catch (error) {
      console.error(`Error ${action} event:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAttendanceAction = async (userId, action) => {
    setActionLoading(`${action}-${userId}`);
    try {
      await axios.post(
        `${API}/admin/events/${selectedEvent}/${action}/${userId}`,
        {},
        { withCredentials: true }
      );
      await loadEventDetail(selectedEvent);
    } catch (error) {
      console.error('Error updating attendance:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Event List View
  if (!selectedEvent) {
    return (
      <div>
        <button 
          onClick={onCreateEvent}
          className="btn-gold flex items-center gap-2 mb-8" 
          data-testid="create-event"
        >
          <Plus size={18} />
          {t('admin_create_event') || 'Crear Evento'}
        </button>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {events.map(event => (
            <div 
              key={event.event_id} 
              className="card-premium p-6 cursor-pointer hover:border-[#8B1A1A]/50 transition-all"
              onClick={() => loadEventDetail(event.event_id)}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="label-premium">{event.event_type}</span>
                  <span className={`px-2 py-1 text-xs ${
                    event.status === 'confirmed' ? 'bg-emerald-900/30 text-emerald-400' :
                    event.status === 'in_progress' ? 'bg-amber-900/30 text-amber-400 animate-pulse' :
                    event.status === 'inviting' ? 'bg-[#8B1A1A]/20 text-[#8B1A1A]' :
                    event.status === 'completed' ? 'bg-[#8A8A8A]/20 text-[#8A8A8A]' :
                    event.status === 'cancelled' ? 'bg-red-900/20 text-red-400' :
                    'bg-[#8B1A1A]/10 text-[#8B1A1A]'
                  }`}>
                    {event.status === 'in_progress' ? 'EN VIVO' : event.status}
                  </span>
                </div>
                <ChevronRight size={20} className="text-[#8A8A8A]" />
              </div>
              <h3 className="text-xl font-serif text-[#F5F0E6] mb-2">{event.title}</h3>
              <p className="text-[#8A8A8A] text-sm mb-4 line-clamp-2">{event.description}</p>
              <div className="flex items-center justify-between text-sm text-[#8A8A8A]">
                <span className="flex items-center gap-2">
                  <Calendar size={14} />
                  {new Date(event.date).toLocaleDateString('es-ES')}
                </span>
                <span className="flex items-center gap-2">
                  <Users size={14} />
                  {event.current_capacity || 0} / {event.max_capacity}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {events.length === 0 && (
          <div className="card-premium p-12 text-center">
            <Calendar className="w-12 h-12 text-[#8B1A1A] mx-auto mb-4 opacity-50" />
            <p className="text-[#8A8A8A]">{t('admin_no_events') || 'No hay eventos'}</p>
          </div>
        )}
      </div>
    );
  }

  // Event Detail View
  const event = eventDetail?.event || {};
  const stats = eventDetail?.stats || {};
  const balance = eventDetail?.balance || {};
  const invitations = eventDetail?.invitations || [];
  const attendance = eventDetail?.attendance || [];

  return (
    <div>
      {/* Back button */}
      <button 
        onClick={() => setSelectedEvent(null)}
        className="flex items-center gap-2 text-[#8A8A8A] hover:text-[#F5F0E6] mb-6"
      >
        <ArrowLeft size={18} />
        Volver a eventos
      </button>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[#8B1A1A]" />
        </div>
      ) : (
        <>
          {/* Event Header */}
          <div className="card-premium p-8 mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-3xl font-serif text-[#F5F0E6]">{event.title}</h2>
                  <span className={`px-3 py-1 text-xs ${
                    event.status === 'confirmed' ? 'bg-emerald-900/30 text-emerald-400' :
                    event.status === 'in_progress' ? 'bg-amber-900/30 text-amber-400 animate-pulse' :
                    event.status === 'inviting' ? 'bg-[#8B1A1A]/20 text-[#8B1A1A]' :
                    event.status === 'completed' ? 'bg-[#8A8A8A]/20 text-[#8A8A8A]' :
                    'bg-[#8B1A1A]/10 text-[#8B1A1A]'
                  }`}>
                    {event.status === 'in_progress' ? 'EN VIVO' : event.status}
                  </span>
                </div>
                <p className="text-[#8A8A8A]">{event.description}</p>
              </div>
              <div className="flex gap-2">
                {event.status === 'draft' && (
                  <button
                    onClick={() => handleEventAction('publish')}
                    disabled={actionLoading === 'publish'}
                    className="btn-gold flex items-center gap-2"
                  >
                    {actionLoading === 'publish' ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                    Publicar
                  </button>
                )}
                {event.status === 'inviting' && (
                  <button
                    onClick={() => handleEventAction('confirm')}
                    disabled={actionLoading === 'confirm'}
                    className="btn-gold flex items-center gap-2"
                  >
                    {actionLoading === 'confirm' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Confirmar
                  </button>
                )}
                {event.status === 'confirmed' && (
                  <button
                    onClick={() => handleEventAction('start-live')}
                    disabled={actionLoading === 'start-live'}
                    className="btn-wine flex items-center gap-2"
                    data-testid="start-live-btn"
                  >
                    {actionLoading === 'start-live' ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />}
                    Iniciar Evento en Vivo
                  </button>
                )}
                {event.status === 'in_progress' && (
                  <>
                    <button
                      onClick={() => setActiveEventTab('live')}
                      className="btn-wine flex items-center gap-2"
                      data-testid="live-dashboard-btn"
                    >
                      <Activity size={16} />
                      Dashboard en Vivo
                    </button>
                    <button
                      onClick={() => handleEventAction('end-live')}
                      disabled={actionLoading === 'end-live'}
                      className="px-4 py-2 border border-[#8B1A1A]/50 text-[#8B1A1A] hover:bg-[#8B1A1A]/10 flex items-center gap-2"
                      data-testid="end-live-btn"
                    >
                      {actionLoading === 'end-live' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                      Finalizar en Vivo
                    </button>
                  </>
                )}
                {(event.status === 'confirmed' || event.status === 'in_progress') && (
                  <button
                    onClick={() => handleEventAction('complete')}
                    disabled={actionLoading === 'complete'}
                    className="px-4 py-2 border border-[#8A8A8A]/30 text-[#8A8A8A] hover:bg-[#161616] flex items-center gap-2"
                  >
                    {actionLoading === 'complete' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Completar
                  </button>
                )}
                {event.status !== 'completed' && event.status !== 'cancelled' && (
                  <button
                    onClick={() => handleEventAction('cancel')}
                    disabled={actionLoading === 'cancel'}
                    className="px-4 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>

            {/* Event Info */}
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-[#8A8A8A] mb-1">Fecha</p>
                <p className="text-[#F5F0E6]">{new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              </div>
              <div>
                <p className="text-[#8A8A8A] mb-1">Lugar</p>
                <p className="text-[#F5F0E6]">{event.venue_name}, {event.city}</p>
              </div>
              <div>
                <p className="text-[#8A8A8A] mb-1">Capacidad</p>
                <p className="text-[#F5F0E6]">{stats.confirmed || 0} / {event.max_capacity}</p>
              </div>
              <div>
                <p className="text-[#8A8A8A] mb-1">Tipo</p>
                <p className="text-[#F5F0E6]">{event.event_type}</p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-[#8B1A1A]">{stats.total_invited || 0}</p>
              <p className="text-[#8A8A8A] text-xs">Invitados</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-emerald-400">{stats.accepted || 0}</p>
              <p className="text-[#8A8A8A] text-xs">Aceptados</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-yellow-400">{stats.pending || 0}</p>
              <p className="text-[#8A8A8A] text-xs">Pendientes</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-red-400">{stats.declined || 0}</p>
              <p className="text-[#8A8A8A] text-xs">Rechazados</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-[#F5F0E6]">{stats.attended || 0}</p>
              <p className="text-[#8A8A8A] text-xs">Asistieron</p>
            </div>
          </div>

          {/* Alerts - Intelligent Balance Alerts with ACTIONS */}
          {balance.alerts?.length > 0 && (
            <div className="mb-8 space-y-2">
              {balance.alerts.map((alert, i) => {
                const isObject = typeof alert === 'object';
                const alertType = isObject ? alert.type : 'warning';
                const category = isObject ? alert.category : 'general';
                const message = isObject ? alert.message : alert;
                const detail = isObject ? alert.detail : null;
                const recommendation = isObject ? alert.recommendation : null;
                
                const bgClass = alertType === 'success' ? 'bg-emerald-900/20 border-emerald-500/30 text-emerald-400' :
                               alertType === 'info' ? 'bg-blue-900/20 border-blue-500/30 text-blue-400' :
                               'bg-yellow-900/20 border-yellow-500/30 text-yellow-400';
                
                const Icon = alertType === 'success' ? CheckCircle : 
                            alertType === 'info' ? AlertCircle : AlertTriangle;
                
                // Define actions based on alert category
                const getAlertAction = () => {
                  if (alertType === 'success') return null;
                  
                  switch(category) {
                    case 'response_rate':
                      return {
                        label: 'Ver Pendientes',
                        action: () => setActiveEventTab('invitations'),
                        icon: Users
                      };
                    case 'gender_balance':
                      return {
                        label: 'Ver Sugeridos',
                        action: () => setActiveEventTab('suggested'),
                        icon: UserPlus
                      };
                    case 'interest_diversity':
                    case 'occupation_diversity':
                      return {
                        label: 'Buscar Perfiles Diversos',
                        action: () => setActiveEventTab('suggested'),
                        icon: Search
                      };
                    case 'intention_mismatch':
                      return {
                        label: 'Revisar Grupo',
                        action: () => setActiveEventTab('invitations'),
                        icon: Filter
                      };
                    case 'social_energy':
                      return {
                        label: 'Ver Balance',
                        action: () => setActiveEventTab('overview'),
                        icon: BarChart2
                      };
                    case 'age_balance':
                      return {
                        label: 'Ajustar Grupo',
                        action: () => setActiveEventTab('suggested'),
                        icon: Users
                      };
                    default:
                      return null;
                  }
                };
                
                const alertAction = getAlertAction();
                
                return (
                  <div key={i} className={`p-3 border text-sm ${bgClass}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        <span className="font-medium">{message}</span>
                      </div>
                      {alertAction && (
                        <button
                          onClick={alertAction.action}
                          className="flex items-center gap-1 px-3 py-1 bg-white/10 hover:bg-white/20 border border-white/20 text-xs transition-colors"
                        >
                          <alertAction.icon size={12} />
                          {alertAction.label}
                        </button>
                      )}
                    </div>
                    {detail && (
                      <p className="ml-6 mt-1 text-xs opacity-80">{detail}</p>
                    )}
                    {recommendation && (
                      <p className="ml-6 mt-1 text-xs italic opacity-70">💡 {recommendation}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Event Tabs */}
          <div className="flex gap-4 mb-6 border-b border-[#8B1A1A]/10">
            {['overview', 'suggested', 'invitations', 'attendance', ...(event.status === 'in_progress' ? ['live'] : [])].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveEventTab(tab)}
                className={`pb-3 px-2 border-b-2 transition-colors ${
                  activeEventTab === tab
                    ? 'border-[#8B1A1A] text-[#8B1A1A]'
                    : 'border-transparent text-[#8A8A8A] hover:text-[#F5F0E6]'
                }`}
                data-testid={`event-tab-${tab}`}
              >
                {tab === 'overview' && 'Vista General'}
                {tab === 'suggested' && `Sugeridos (${suggestedParticipants.length})`}
                {tab === 'invitations' && `Invitaciones (${invitations.length})`}
                {tab === 'attendance' && `Asistencia (${attendance.length})`}
                {tab === 'live' && (
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    En Vivo
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeEventTab === 'overview' && (
            <div className="grid grid-cols-2 gap-8">
              <div className="card-premium p-6">
                <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">Balance del Grupo</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-[#8A8A8A] text-sm">Rango de Edades</p>
                    <p className="text-[#F5F0E6]">
                      {balance.age_range?.min || '?'} - {balance.age_range?.max || '?'} años
                    </p>
                  </div>
                  <div>
                    <p className="text-[#8A8A8A] text-sm">Ciudades</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {Object.entries(balance.cities_distribution || {}).map(([city, count]) => (
                        <span key={city} className="text-xs px-2 py-1 bg-[#8B1A1A]/10 text-[#F5F0E6]">
                          {city}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-premium p-6">
                <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">Métricas</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#8A8A8A]">Tasa de Aceptación</span>
                    <span className="text-[#F5F0E6]">
                      {stats.total_invited > 0 ? Math.round((stats.accepted / stats.total_invited) * 100) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8A8A8A]">Ocupación</span>
                    <span className="text-[#F5F0E6]">
                      {Math.round(((stats.confirmed || 0) / event.max_capacity) * 100)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8A8A8A]">Cupos Restantes</span>
                    <span className="text-[#F5F0E6]">{stats.capacity_remaining}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Suggested Participants Tab */}
          {activeEventTab === 'suggested' && (
            <div className="space-y-3">
              {suggestedParticipants.map(candidate => (
                <div key={candidate.user.user_id} className="card-premium p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#161616] border border-[#8B1A1A]/30 flex items-center justify-center">
                      <span className="text-[#8B1A1A]">{candidate.user.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-[#F5F0E6]">{candidate.user.name}</p>
                      <p className="text-[#8A8A8A] text-sm">{candidate.user.age} años • {candidate.user.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-[#8B1A1A] font-medium">{candidate.suitability_score}%</p>
                      <p className="text-[#8A8A8A] text-xs">{candidate.reasons.slice(0, 2).join(', ')}</p>
                    </div>
                    <button
                      onClick={() => handleInviteUser(candidate.user.user_id)}
                      disabled={actionLoading === candidate.user.user_id}
                      className="btn-outline-gold text-sm"
                    >
                      {actionLoading === candidate.user.user_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        'Invitar'
                      )}
                    </button>
                  </div>
                </div>
              ))}
              {suggestedParticipants.length === 0 && (
                <div className="text-center py-10 text-[#8A8A8A]">
                  No hay más candidatos sugeridos
                </div>
              )}
            </div>
          )}

          {/* Invitations Tab */}
          {activeEventTab === 'invitations' && (
            <div className="space-y-3">
              {invitations.map(inv => (
                <div key={inv.invitation_id} className="card-premium p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#161616] border border-[#8B1A1A]/30 flex items-center justify-center">
                      <span className="text-[#8B1A1A]">{inv.user?.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-[#F5F0E6]">{inv.user?.name}</p>
                      <p className="text-[#8A8A8A] text-sm">{inv.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-xs ${
                      inv.invitation_status === 'accepted' ? 'bg-emerald-900/30 text-emerald-400' :
                      inv.invitation_status === 'declined' ? 'bg-red-900/30 text-red-400' :
                      inv.invitation_status === 'viewed' ? 'bg-blue-900/30 text-blue-400' :
                      'bg-[#8B1A1A]/20 text-[#8B1A1A]'
                    }`}>
                      {inv.invitation_status}
                    </span>
                    <button
                      onClick={() => handleRemoveInvitation(inv.user?.user_id)}
                      disabled={actionLoading === inv.user?.user_id}
                      className="p-2 text-[#8A8A8A] hover:text-red-400"
                    >
                      {actionLoading === inv.user?.user_id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <XCircle size={16} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
              {invitations.length === 0 && (
                <div className="text-center py-10 text-[#8A8A8A]">
                  No hay invitaciones aún. Ve a "Sugeridos" para invitar participantes.
                </div>
              )}
            </div>
          )}

          {/* Attendance Tab */}
          {activeEventTab === 'attendance' && (
            <div className="space-y-3">
              {attendance.map(att => (
                <div key={att.attendance_id} className="card-premium p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#161616] border border-[#8B1A1A]/30 flex items-center justify-center">
                      <span className="text-[#8B1A1A]">{att.user?.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-[#F5F0E6]">{att.user?.name}</p>
                      <p className="text-[#8A8A8A] text-sm">{att.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs ${
                      att.attendance_status === 'attended' ? 'bg-emerald-900/30 text-emerald-400' :
                      att.attendance_status === 'checked_in' ? 'bg-blue-900/30 text-blue-400' :
                      att.attendance_status === 'no_show' ? 'bg-red-900/30 text-red-400' :
                      'bg-[#8B1A1A]/20 text-[#8B1A1A]'
                    }`}>
                      {att.attendance_status}
                    </span>
                    {event.status === 'confirmed' && att.attendance_status === 'confirmed' && (
                      <button
                        onClick={() => handleAttendanceAction(att.user?.user_id, 'check-in')}
                        disabled={actionLoading === `check-in-${att.user?.user_id}`}
                        className="btn-outline-gold text-xs"
                      >
                        Check-in
                      </button>
                    )}
                    {att.attendance_status === 'checked_in' && (
                      <>
                        <button
                          onClick={() => handleAttendanceAction(att.user?.user_id, 'mark-attended')}
                          disabled={actionLoading === `mark-attended-${att.user?.user_id}`}
                          className="text-xs px-2 py-1 bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/50"
                        >
                          Asistió
                        </button>
                        <button
                          onClick={() => handleAttendanceAction(att.user?.user_id, 'mark-no-show')}
                          disabled={actionLoading === `mark-no-show-${att.user?.user_id}`}
                          className="text-xs px-2 py-1 bg-red-900/30 text-red-400 hover:bg-red-900/50"
                        >
                          No Show
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {attendance.length === 0 && (
                <div className="text-center py-10 text-[#8A8A8A]">
                  No hay registros de asistencia aún.
                </div>
              )}
            </div>
          )}

          {/* LIVE DASHBOARD TAB */}
          {activeEventTab === 'live' && event.status === 'in_progress' && (
            <AdminLiveDashboard 
              eventId={selectedEvent} 
              onRefresh={() => loadEventDetail(selectedEvent)}
            />
          )}
        </>
      )}
    </div>
  );
};

// ============== ADMIN LIVE DASHBOARD ==============
const AdminLiveDashboard = ({ eventId, onRefresh }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastType, setBroadcastType] = useState('info');
  const [broadcastDuration, setBroadcastDuration] = useState(15);
  const [heatMap, setHeatMap] = useState(null);
  const [showHeatMap, setShowHeatMap] = useState(false);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/events/${eventId}/live-dashboard`, { withCredentials: true });
      setDashboard(res.data);
    } catch (err) {
      console.error('Error fetching live dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeatMap = async () => {
    try {
      const res = await axios.get(`${API}/admin/events/${eventId}/heat-map`, { withCredentials: true });
      setHeatMap(res.data);
    } catch (err) {
      console.error('Error fetching heat map:', err);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 5000);
    return () => clearInterval(interval);
  }, [eventId]);

  const handleStartRound = async () => {
    setActionLoading('start-round');
    try {
      await axios.post(`${API}/admin/events/${eventId}/start-round`, {}, { withCredentials: true });
      await fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al iniciar ronda');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEndRound = async () => {
    setActionLoading('end-round');
    try {
      await axios.post(`${API}/admin/events/${eventId}/end-round`, {}, { withCredentials: true });
      await fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al terminar ronda');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceAdvance = async () => {
    setActionLoading('force-advance');
    try {
      await axios.post(`${API}/admin/events/${eventId}/force-advance`, {}, { withCredentials: true });
      await fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al forzar avance');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastMsg.trim()) return;
    setActionLoading('broadcast');
    try {
      await axios.post(`${API}/admin/events/${eventId}/broadcast`, {
        message: broadcastMsg,
        type: broadcastType,
        display_duration_seconds: broadcastDuration
      }, { withCredentials: true });
      setBroadcastMsg('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar broadcast');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleHeatMap = () => {
    if (!showHeatMap) fetchHeatMap();
    setShowHeatMap(!showHeatMap);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={32} className="animate-spin text-[#8B1A1A]" />
      </div>
    );
  }

  if (!dashboard) return <p className="text-[#8A8A8A]">No se pudo cargar el dashboard en vivo</p>;

  const { live_state, rounds, active_round, current_assignments, attended_users, feedback_summary } = dashboard;
  const currentRound = live_state?.current_round_number || 0;
  const totalRounds = live_state?.total_rounds || 5;
  const hasActiveRound = !!active_round;

  return (
    <div className="space-y-6" data-testid="admin-live-dashboard">
      {/* Live status bar */}
      <div className="bg-gradient-to-r from-[#8B1A1A]/20 to-transparent border border-[#8B1A1A]/30 p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
          <div>
            <p className="text-[#F5F0E6] font-serif text-lg">Evento en Vivo</p>
            <p className="text-[#8A8A8A] text-sm">Ronda {currentRound} de {totalRounds}</p>
          </div>
        </div>
        <div className="flex gap-3">
          {!hasActiveRound && currentRound < totalRounds && (
            <button
              onClick={handleStartRound}
              disabled={actionLoading === 'start-round'}
              className="btn-wine flex items-center gap-2 px-6 py-3"
              data-testid="admin-start-round-btn"
            >
              {actionLoading === 'start-round' ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
              Iniciar Ronda {currentRound + 1}
            </button>
          )}
          {hasActiveRound && (
            <>
              <button
                onClick={handleEndRound}
                disabled={actionLoading === 'end-round'}
                className="px-4 py-2 border border-[#8B1A1A]/50 text-[#8B1A1A] hover:bg-[#8B1A1A]/10 flex items-center gap-2"
                data-testid="admin-end-round-btn"
              >
                {actionLoading === 'end-round' ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />}
                Terminar Ronda
              </button>
              <button
                onClick={handleForceAdvance}
                disabled={actionLoading === 'force-advance'}
                className="px-4 py-2 border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 flex items-center gap-2"
                data-testid="admin-force-advance-btn"
              >
                {actionLoading === 'force-advance' ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                Forzar Avance
              </button>
            </>
          )}
        </div>
      </div>

      {/* Feedback summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-premium p-5 text-center">
          <p className="text-3xl font-serif text-emerald-400">{feedback_summary?.positive || 0}</p>
          <p className="text-[#8A8A8A] text-xs mt-1">Buena conexión</p>
        </div>
        <div className="card-premium p-5 text-center">
          <p className="text-3xl font-serif text-amber-400">{feedback_summary?.neutral || 0}</p>
          <p className="text-[#8A8A8A] text-xs mt-1">Agradable</p>
        </div>
        <div className="card-premium p-5 text-center">
          <p className="text-3xl font-serif text-[#666]">{feedback_summary?.negative || 0}</p>
          <p className="text-[#8A8A8A] text-xs mt-1">Sin conexión</p>
        </div>
      </div>

      {/* Current round assignments */}
      {hasActiveRound && current_assignments.length > 0 && (
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">
            Interacciones - Ronda {active_round.round_number}: {active_round.round_theme}
          </h3>
          <div className="space-y-3">
            {current_assignments.map(a => (
              <div key={a.assignment_id} className="card-premium p-4 flex items-center justify-between" data-testid={`assignment-${a.assignment_id}`}>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-[#F5F0E6] text-sm font-medium">{a.user_a_name}</p>
                    <div className={`mt-1 w-6 h-6 rounded-full mx-auto flex items-center justify-center text-xs ${
                      a.user_a_feedback?.submitted 
                        ? a.user_a_feedback.rating === 3 ? 'bg-emerald-900/40 text-emerald-400'
                          : a.user_a_feedback.rating === 2 ? 'bg-amber-900/40 text-amber-400'
                          : 'bg-[#333] text-[#666]'
                        : 'bg-[#222] text-[#555] animate-pulse'
                    }`}>
                      {a.user_a_feedback?.submitted ? (a.user_a_feedback.rating === 3 ? '3' : a.user_a_feedback.rating === 2 ? '2' : '1') : '?'}
                    </div>
                  </div>
                  <span className="text-[#8B1A1A]">—</span>
                  <div className="text-center">
                    <p className="text-[#F5F0E6] text-sm font-medium">{a.user_b_name}</p>
                    <div className={`mt-1 w-6 h-6 rounded-full mx-auto flex items-center justify-center text-xs ${
                      a.user_b_feedback?.submitted 
                        ? a.user_b_feedback.rating === 3 ? 'bg-emerald-900/40 text-emerald-400'
                          : a.user_b_feedback.rating === 2 ? 'bg-amber-900/40 text-amber-400'
                          : 'bg-[#333] text-[#666]'
                        : 'bg-[#222] text-[#555] animate-pulse'
                    }`}>
                      {a.user_b_feedback?.submitted ? (a.user_b_feedback.rating === 3 ? '3' : a.user_b_feedback.rating === 2 ? '2' : '1') : '?'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.user_a_feedback?.submitted && a.user_b_feedback?.submitted ? (
                    <span className="text-emerald-400 text-xs">Feedback completo</span>
                  ) : (
                    <span className="text-amber-400 text-xs animate-pulse">Esperando feedback</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Round history */}
      {rounds.length > 0 && (
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">Historial de Rondas</h3>
          <div className="space-y-2">
            {rounds.map(r => (
              <div key={r.round_id} className="card-premium p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-serif ${
                    r.status === 'active' ? 'bg-[#8B1A1A]/30 text-[#8B1A1A]' :
                    r.status === 'completed' ? 'bg-emerald-900/30 text-emerald-400' :
                    'bg-[#222] text-[#555]'
                  }`}>
                    {r.round_number}
                  </span>
                  <div>
                    <p className="text-[#F5F0E6] text-sm">{r.round_theme}</p>
                    <p className="text-[#8A8A8A] text-xs">{r.interactions_count} interacciones</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 ${
                  r.status === 'active' ? 'bg-amber-900/30 text-amber-400 animate-pulse' :
                  r.status === 'completed' ? 'bg-emerald-900/30 text-emerald-400' :
                  'bg-[#222] text-[#555]'
                }`}>
                  {r.status === 'active' ? 'Activa' : r.status === 'completed' ? 'Completada' : r.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attended users with feedback status */}
      <div>
        <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">
          Participantes ({attended_users?.length || 0})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {attended_users?.map(u => (
            <div key={u.user_id} className="card-premium p-4 flex items-center justify-between" data-testid={`live-user-${u.user_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#161616] border border-[#8B1A1A]/20 flex items-center justify-center text-[#8B1A1A] text-sm font-serif">
                  {u.name?.[0]}
                </div>
                <div>
                  <p className="text-[#F5F0E6] text-sm">{u.name}</p>
                  <p className="text-[#8A8A8A] text-xs">{u.gender} &bull; {u.total_interactions} interacciones</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#8A8A8A] text-xs">{u.feedback_given} fb</span>
                {u.has_pending_feedback ? (
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" title="Feedback pendiente" />
                ) : (
                  <span className="w-2 h-2 bg-emerald-400 rounded-full" title="Al día" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BROADCAST CONTROLS */}
      <div className="card-premium p-6" data-testid="admin-broadcast-panel">
        <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-3">
          <Activity size={20} className="text-[#8B1A1A]" />
          Broadcast en Vivo
        </h3>
        <div className="space-y-4">
          <textarea
            value={broadcastMsg}
            onChange={e => setBroadcastMsg(e.target.value)}
            placeholder="Escribe tu mensaje para todos los participantes..."
            className="w-full bg-[#111] border border-[#333] text-[#F5F0E6] p-4 text-sm focus:outline-none focus:border-[#8B1A1A] transition-colors placeholder:text-[#555] resize-none"
            rows={2}
            data-testid="broadcast-message-input"
          />
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex gap-2">
              {[
                { value: 'info', label: 'Aviso', color: '#C4A76B' },
                { value: 'dinamica', label: 'Dinámica', color: '#8B1A1A' },
                { value: 'instruccion', label: 'Instrucción', color: '#B82828' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setBroadcastType(opt.value)}
                  className={`px-3 py-1.5 text-xs border transition-all ${
                    broadcastType === opt.value
                      ? 'border-current bg-current/10'
                      : 'border-[#333] text-[#8A8A8A] hover:border-[#555]'
                  }`}
                  style={broadcastType === opt.value ? { color: opt.color, borderColor: opt.color } : {}}
                  data-testid={`broadcast-type-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <select
              value={broadcastDuration}
              onChange={e => setBroadcastDuration(Number(e.target.value))}
              className="bg-[#111] border border-[#333] text-[#8A8A8A] px-3 py-1.5 text-xs focus:outline-none"
              data-testid="broadcast-duration-select"
            >
              <option value={10}>10 seg</option>
              <option value={15}>15 seg</option>
              <option value={20}>20 seg</option>
              <option value={30}>30 seg</option>
              <option value={60}>1 min</option>
            </select>
            <button
              onClick={handleSendBroadcast}
              disabled={!broadcastMsg.trim() || actionLoading === 'broadcast'}
              className={`btn-wine flex items-center gap-2 px-6 py-2 ml-auto ${
                !broadcastMsg.trim() ? 'opacity-40 cursor-not-allowed' : ''
              }`}
              data-testid="send-broadcast-btn"
            >
              {actionLoading === 'broadcast' ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
              Enviar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              'Cambien de mesa',
              'Hagan una pregunta más profunda',
              'Tienen 5 minutos más',
              'Nueva dinámica: hablen de viajes',
              'Última oportunidad de la ronda'
            ].map(quick => (
              <button
                key={quick}
                onClick={() => setBroadcastMsg(quick)}
                className="px-3 py-1.5 text-xs border border-[#222] text-[#666] hover:border-[#8B1A1A]/40 hover:text-[#8A8A8A] transition-colors"
                data-testid={`quick-broadcast-${quick.slice(0,10)}`}
              >
                {quick}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* HEAT MAP TOGGLE */}
      <div>
        <button
          onClick={toggleHeatMap}
          className="flex items-center gap-3 text-[#8B1A1A] hover:text-[#B82828] transition-colors mb-4"
          data-testid="toggle-heat-map-btn"
        >
          <BarChart3 size={20} />
          <span className="font-serif text-lg text-[#F5F0E6]">
            {showHeatMap ? 'Ocultar' : 'Ver'} Mapa de Conexiones
          </span>
        </button>

        {showHeatMap && heatMap && (
          <div className="space-y-6" data-testid="heat-map-section">
            {/* Global stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="card-premium p-4 text-center">
                <p className="text-2xl font-serif text-[#8B1A1A]">{heatMap.global_stats?.strong_connections || 0}</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Conexiones fuertes</p>
              </div>
              <div className="card-premium p-4 text-center">
                <p className="text-2xl font-serif text-amber-400">{heatMap.global_stats?.promising_connections || 0}</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Prometedoras</p>
              </div>
              <div className="card-premium p-4 text-center">
                <p className="text-2xl font-serif text-[#666]">{heatMap.global_stats?.cold_connections || 0}</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Frías</p>
              </div>
              <div className="card-premium p-4 text-center">
                <p className="text-2xl font-serif text-[#F5F0E6]">{heatMap.global_stats?.average_heat || 0}</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Calor promedio</p>
              </div>
            </div>

            {/* Connection pairs */}
            <div>
              <h4 className="text-sm text-[#8A8A8A] uppercase tracking-wider mb-3">Interacciones por calor</h4>
              <div className="space-y-2">
                {heatMap.connections?.map((c, idx) => {
                  const heatColors = {
                    5: { bg: 'bg-[#8B1A1A]/20', border: 'border-[#8B1A1A]/50', text: 'text-[#B82828]', label: 'Conexión fuerte' },
                    4: { bg: 'bg-amber-900/20', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Prometedora' },
                    3: { bg: 'bg-[#C4A76B]/10', border: 'border-[#C4A76B]/30', text: 'text-[#C4A76B]', label: 'Potencial' },
                    2: { bg: 'bg-[#222]', border: 'border-[#333]', text: 'text-[#8A8A8A]', label: 'Neutral' },
                    1: { bg: 'bg-[#161616]', border: 'border-[#222]', text: 'text-[#555]', label: 'Fría' }
                  };
                  const h = heatColors[c.heat] || heatColors[2];

                  return (
                    <div key={idx} className={`p-4 border flex items-center justify-between ${h.bg} ${h.border}`} data-testid={`heat-pair-${idx}`}>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-[#F5F0E6] text-sm">{c.user_a_name}</span>
                          <span className={`text-xs font-mono ${
                            c.rating_a_to_b === 3 ? 'text-emerald-400' : c.rating_a_to_b === 2 ? 'text-amber-400' : c.rating_a_to_b === 1 ? 'text-[#666]' : 'text-[#333]'
                          }`}>{c.rating_a_to_b || '—'}</span>
                        </div>
                        <span className="text-[#333]">↔</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-mono ${
                            c.rating_b_to_a === 3 ? 'text-emerald-400' : c.rating_b_to_a === 2 ? 'text-amber-400' : c.rating_b_to_a === 1 ? 'text-[#666]' : 'text-[#333]'
                          }`}>{c.rating_b_to_a || '—'}</span>
                          <span className="text-[#F5F0E6] text-sm">{c.user_b_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {c.mutual_positive && (
                          <span className="text-[#8B1A1A] text-xs border border-[#8B1A1A]/30 px-2 py-0.5">MUTUAL</span>
                        )}
                        {c.should_avoid && (
                          <span className="text-[#555] text-xs border border-[#333] px-2 py-0.5">EVITAR</span>
                        )}
                        <span className={`text-xs ${h.text}`}>{h.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Per-user summary */}
            <div>
              <h4 className="text-sm text-[#8A8A8A] uppercase tracking-wider mb-3">Resumen por participante</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {heatMap.user_summaries?.map(u => (
                  <div key={u.user_id} className="card-premium p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#161616] border border-[#8B1A1A]/20 flex items-center justify-center text-[#8B1A1A] text-xs font-serif">
                        {u.name?.[0]}
                      </div>
                      <div>
                        <p className="text-[#F5F0E6] text-sm">{u.name}</p>
                        <p className="text-[#8A8A8A] text-xs">{u.total_interactions} interacciones</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-emerald-400">{u.positive_connections} buenas</span>
                      <span className="text-[#555]">|</span>
                      <span className="text-amber-400">{u.neutral_connections} neutras</span>
                      <span className="text-[#555]">|</span>
                      <span className="text-[#666]">{u.cold_connections} frías</span>
                      <span className="text-[#555]">|</span>
                      <span className={u.connection_rate >= 50 ? 'text-emerald-400' : 'text-[#8A8A8A]'}>
                        {u.connection_rate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== ADMIN MATCH ANALYTICS ==============
const AdminMatchAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/matches/analytics`, { withCredentials: true });
        setAnalytics(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#8B1A1A]" /></div>;
  if (!analytics) return <p className="text-[#8A8A8A]">Error loading analytics</p>;

  const { funnel, confirmation, chat, event_breakdown, recent_matches } = analytics;

  return (
    <div className="space-y-8" data-testid="admin-match-analytics">
      <div>
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">Match Analytics</h2>
        <p className="text-[#8A8A8A]">Embudo de conversión: match &rarr; confirmación &rarr; chat &rarr; contacto</p>
      </div>

      {/* Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Matches totales', value: funnel.total_matches, color: 'text-[#F5F0E6]' },
          { label: 'Mutual', value: funnel.mutual, color: 'text-[#8B1A1A]' },
          { label: 'Con chat', value: funnel.confirmed_chat, color: 'text-emerald-400' },
          { label: 'Contacto desbloqueado', value: funnel.contact_unlocked, color: 'text-[#C4A76B]' },
          { label: 'Cerrados', value: funnel.closed, color: 'text-[#555]' }
        ].map((s, i) => (
          <div key={i} className="card-premium p-5 text-center">
            <p className={`text-3xl font-serif ${s.color}`}>{s.value}</p>
            <p className="text-[#8A8A8A] text-xs mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Conversion rates */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card-premium p-5">
          <p className="text-[#8A8A8A] text-xs mb-2">Conversión a Chat</p>
          <p className="text-2xl font-serif text-emerald-400">{funnel.conversion_to_chat}%</p>
          <div className="w-full h-2 bg-[#222] mt-3 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${funnel.conversion_to_chat}%` }} />
          </div>
        </div>
        <div className="card-premium p-5">
          <p className="text-[#8A8A8A] text-xs mb-2">Conversión a Contacto</p>
          <p className="text-2xl font-serif text-[#C4A76B]">{funnel.conversion_to_contact}%</p>
          <div className="w-full h-2 bg-[#222] mt-3 rounded-full overflow-hidden">
            <div className="h-full bg-[#C4A76B] rounded-full" style={{ width: `${funnel.conversion_to_contact}%` }} />
          </div>
        </div>
      </div>

      {/* Confirmation & Chat stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-premium p-5 text-center">
          <p className="text-2xl font-serif text-emerald-400">{confirmation.total_yes}</p>
          <p className="text-[#8A8A8A] text-xs mt-1">Confirmaciones "Sí"</p>
        </div>
        <div className="card-premium p-5 text-center">
          <p className="text-2xl font-serif text-[#555]">{confirmation.total_no}</p>
          <p className="text-[#8A8A8A] text-xs mt-1">Declinados</p>
        </div>
        <div className="card-premium p-5 text-center">
          <p className="text-2xl font-serif text-amber-400">{confirmation.pending}</p>
          <p className="text-[#8A8A8A] text-xs mt-1">Pendientes</p>
        </div>
      </div>

      {/* Chat activity */}
      <div className="card-premium p-5 flex items-center justify-between">
        <div>
          <p className="text-[#F5F0E6] font-serif">Actividad de chat</p>
          <p className="text-[#8A8A8A] text-xs">{chat.active_chats} conversaciones activas</p>
        </div>
        <p className="text-2xl font-serif text-[#F5F0E6]">{chat.total_messages} <span className="text-sm text-[#8A8A8A]">mensajes</span></p>
      </div>

      {/* Per-event breakdown */}
      {event_breakdown.length > 0 && (
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">Por Evento</h3>
          <div className="space-y-3">
            {event_breakdown.map((evt, i) => (
              <div key={i} className="card-premium p-4 flex items-center justify-between">
                <div>
                  <p className="text-[#F5F0E6] text-sm">{evt.event_title}</p>
                  <p className="text-[#8A8A8A] text-xs">{evt.total_matches} matches &bull; {evt.confirmed} confirmados &bull; {evt.contact_unlocked} contactos</p>
                </div>
                <span className={`text-sm font-mono ${evt.conversion_rate >= 50 ? 'text-emerald-400' : 'text-[#8A8A8A]'}`}>
                  {evt.conversion_rate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent matches */}
      {recent_matches.length > 0 && (
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">Matches Recientes</h3>
          <div className="space-y-2">
            {recent_matches.map(m => (
              <div key={m.match_id} className="card-premium p-4 flex items-center justify-between" data-testid={`admin-match-${m.match_id}`}>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#F5F0E6]">{m.user_a_name}</span>
                    <span className="text-[#8B1A1A]">&bull;</span>
                    <span className="text-[#F5F0E6]">{m.user_b_name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-[#8A8A8A]">{m.messages_count} msgs</span>
                  <span className={`px-2 py-1 ${
                    m.level === 'contact_unlocked' ? 'bg-[#C4A76B]/15 text-[#C4A76B]' :
                    m.level === 'chat_unlocked' ? 'bg-emerald-900/20 text-emerald-400' :
                    m.level === 'mutual' && m.status === 'closed' ? 'bg-[#222] text-[#555]' :
                    'bg-[#8B1A1A]/15 text-[#8B1A1A]'
                  }`}>
                    {m.level === 'contact_unlocked' ? 'Contacto' :
                     m.level === 'chat_unlocked' ? 'Chat' :
                     m.status === 'closed' ? 'Cerrado' : 'Mutual'}
                  </span>
                  <div className="flex gap-1">
                    <span className={m.user_a_response === 'yes' ? 'text-emerald-400' : m.user_a_response === 'no' ? 'text-[#555]' : 'text-amber-400'}>
                      {m.user_a_response === 'yes' ? 'S' : m.user_a_response === 'no' ? 'N' : '?'}
                    </span>
                    <span className="text-[#333]">/</span>
                    <span className={m.user_b_response === 'yes' ? 'text-emerald-400' : m.user_b_response === 'no' ? 'text-[#555]' : 'text-amber-400'}>
                      {m.user_b_response === 'yes' ? 'S' : m.user_b_response === 'no' ? 'N' : '?'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== PILOT DASHBOARD (Admin) ==============

const PilotDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupResult, setSetupResult] = useState(null);
  const [membershipResult, setMembershipResult] = useState(null);
  const [pilotUserId, setPilotUserId] = useState('');
  const [pilotTier, setPilotTier] = useState('premium');

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await axios.get(`${API}/admin/pilot/metrics`, { withCredentials: true });
        setMetrics(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchMetrics();
  }, []);

  const handleSetMembership = async () => {
    if (!pilotUserId.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/membership/set`, {
        user_id: pilotUserId, tier: pilotTier
      }, { withCredentials: true });
      setMembershipResult(res.data);
      setPilotUserId('');
    } catch (err) {
      setMembershipResult({ error: err.response?.data?.detail || 'Error' });
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#8B1A1A]" /></div>;

  return (
    <div className="space-y-8" data-testid="pilot-dashboard">
      <div>
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">Panel de Piloto</h2>
        <p className="text-[#8A8A8A]">
          Herramientas para configurar y medir eventos piloto con usuarios reales.
        </p>
      </div>

      {/* Pilot Metrics */}
      {metrics && (
        <>
          <div>
            <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-4">Funnel de conversion</p>
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: 'Matches', value: metrics.funnel.total_matches, color: 'text-[#F5F0E6]' },
                { label: 'Confirmados', value: metrics.funnel.confirmed, color: 'text-[#C4A76B]' },
                { label: 'Chat activo', value: metrics.funnel.chat_active, color: 'text-emerald-400' },
                { label: 'Contacto', value: metrics.funnel.contact_unlocked, color: 'text-[#8B1A1A]' },
                { label: 'Cerrados', value: metrics.funnel.closed, color: 'text-[#555]' },
              ].map((item, i) => (
                <div key={i} className="card-premium p-4 text-center">
                  <p className={`text-2xl font-serif ${item.color}`}>{item.value}</p>
                  <p className="text-[#8A8A8A] text-xs mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-4">Comportamiento real</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="card-premium p-5 text-center">
                <p className="text-2xl font-serif text-[#F5F0E6]">{metrics.behavior.avg_response_time_minutes} min</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Tiempo promedio de respuesta</p>
                <p className="text-[#555] text-xs">{metrics.behavior.total_response_samples} muestras</p>
              </div>
              <div className="card-premium p-5 text-center">
                <p className="text-2xl font-serif text-[#F5F0E6]">{metrics.behavior.avg_confirmation_time_hours}h</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Tiempo promedio de confirmacion</p>
                <p className="text-[#555] text-xs">{metrics.behavior.total_confirmation_samples} muestras</p>
              </div>
              <div className="card-premium p-5 text-center">
                <p className="text-2xl font-serif text-emerald-400">{metrics.ice_breakers.usage_rate}</p>
                <p className="text-[#8A8A8A] text-xs mt-1">Uso de Ice Breakers</p>
                <p className="text-[#555] text-xs">{metrics.ice_breakers.used} usados, {metrics.ice_breakers.dismissed} descartados</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Admin: Set Membership */}
      <div>
        <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-4">Gestionar membresia</p>
        <div className="card-premium p-5">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-[#8A8A8A] text-xs block mb-1">User ID</label>
              <input
                type="text"
                value={pilotUserId}
                onChange={e => setPilotUserId(e.target.value)}
                placeholder="user_..."
                className="w-full bg-[#111] border border-[#333] text-[#F5F0E6] px-3 py-2 text-sm focus:outline-none focus:border-[#8B1A1A]"
                data-testid="pilot-user-id-input"
              />
            </div>
            <div>
              <label className="text-[#8A8A8A] text-xs block mb-1">Tier</label>
              <select
                value={pilotTier}
                onChange={e => setPilotTier(e.target.value)}
                className="bg-[#111] border border-[#333] text-[#F5F0E6] px-3 py-2 text-sm focus:outline-none"
                data-testid="pilot-tier-select"
              >
                <option value="free">Free</option>
                <option value="premium">Premium</option>
                <option value="premium_trial">Trial (7 dias)</option>
              </select>
            </div>
            <button
              onClick={handleSetMembership}
              className="btn-wine px-4 py-2 text-sm"
              data-testid="pilot-set-membership-btn"
            >
              Actualizar
            </button>
          </div>
          {membershipResult && (
            <p className={`text-xs mt-3 ${membershipResult.error ? 'text-red-400' : 'text-emerald-400'}`}>
              {membershipResult.message || membershipResult.error}
            </p>
          )}
        </div>
      </div>

      {setupResult && (
        <div className="card-premium p-4 bg-emerald-900/10 border-emerald-500/20">
          <p className="text-emerald-400 text-sm">{setupResult.message}</p>
        </div>
      )}
    </div>
  );
};

// ============== ADMIN PROACTIVE ALERTS DASHBOARD ==============

const AdminAlertsDashboard = () => {
  const [data, setData] = useState(null);
  const [iceData, setIceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [alertsRes, iceRes] = await Promise.all([
          axios.get(`${API}/admin/alerts/at-risk`, { withCredentials: true }),
          axios.get(`${API}/admin/analytics/ice-breakers`, { withCredentials: true })
        ]);
        setData(alertsRes.data);
        setIceData(iceRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#8B1A1A]" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-alerts-dashboard">
      <div>
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">Conexiones que requieren atencion</h2>
        <p className="text-[#8A8A8A]">
          Monitoreo proactivo. El admin observa y puede enviar estimulos generales, nunca manipular conexiones.
        </p>
      </div>

      {/* Severity summary */}
      {data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card-premium p-5 text-center border border-red-500/20">
            <p className="text-3xl font-serif text-red-400">{data.by_severity.high}</p>
            <p className="text-xs text-[#8A8A8A] mt-1">Urgente</p>
          </div>
          <div className="card-premium p-5 text-center border border-amber-500/20">
            <p className="text-3xl font-serif text-amber-400">{data.by_severity.medium}</p>
            <p className="text-xs text-[#8A8A8A] mt-1">Atencion</p>
          </div>
          <div className="card-premium p-5 text-center border border-[#C4A76B]/20">
            <p className="text-3xl font-serif text-[#C4A76B]">{data.by_severity.low}</p>
            <p className="text-xs text-[#8A8A8A] mt-1">Oportunidad</p>
          </div>
        </div>
      )}

      {/* Alert list */}
      {data && data.alerts.length > 0 ? (
        <div className="space-y-3">
          {data.alerts.map((alert, i) => (
            <div
              key={i}
              className={`card-premium p-5 border ${
                alert.severity === 'high' ? 'border-red-500/20' :
                alert.severity === 'medium' ? 'border-amber-500/20' :
                'border-[#C4A76B]/20'
              }`}
              data-testid={`alert-${i}`}
            >
              <div className="flex items-start gap-4">
                {alert.severity === 'high' ? <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" /> :
                 alert.severity === 'medium' ? <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" /> :
                 <Sparkles size={18} className="text-[#C4A76B] flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[#F5F0E6] text-sm">{alert.user_a_name}</span>
                    <Heart size={10} className="text-[#8B1A1A]" />
                    <span className="text-[#F5F0E6] text-sm">{alert.user_b_name}</span>
                  </div>
                  <p className="text-[#8A8A8A] text-sm">{alert.message}</p>
                  <p className="text-[#555] text-xs mt-1">{alert.detail}</p>
                </div>
                <span className={`text-xs px-2 py-1 ${
                  alert.type === 'inactive' ? 'bg-red-900/20 text-red-400' :
                  alert.type === 'low_quality' ? 'bg-amber-900/20 text-amber-400' :
                  alert.type === 'no_activity' ? 'bg-red-900/20 text-red-400' :
                  'bg-[#C4A76B]/15 text-[#C4A76B]'
                }`}>
                  {alert.type === 'inactive' ? 'Inactivo' :
                   alert.type === 'low_quality' ? 'Baja calidad' :
                   alert.type === 'no_activity' ? 'Sin actividad' :
                   'Alto potencial'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-premium p-10 text-center">
          <CheckCircle size={32} className="text-emerald-400 mx-auto mb-4" />
          <p className="text-[#F5F0E6] font-serif text-lg">Todo en orden</p>
          <p className="text-[#8A8A8A] text-sm mt-1">No hay conexiones en riesgo actualmente</p>
        </div>
      )}

      {/* Ice Breaker Analytics */}
      {iceData && (
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">Efectividad de Ice Breakers</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-[#F5F0E6]">{iceData.total_generated}</p>
              <p className="text-xs text-[#8A8A8A] mt-1">Generados</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-emerald-400">{iceData.usage.used}</p>
              <p className="text-xs text-[#8A8A8A] mt-1">Usados</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-amber-400">{iceData.usage.regenerated}</p>
              <p className="text-xs text-[#8A8A8A] mt-1">Regenerados</p>
            </div>
            <div className="card-premium p-4 text-center">
              <p className="text-2xl font-serif text-[#C4A76B]">{iceData.effectiveness.conversion_rate}%</p>
              <p className="text-xs text-[#8A8A8A] mt-1">Conversion</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== EXPLAINABLE AI DASHBOARD ==============

const StrengthBadge = ({ strength }) => {
  const config = {
    'muy fuerte': 'bg-emerald-900/30 text-emerald-400 border-emerald-500/20',
    'fuerte': 'bg-[#C4A76B]/15 text-[#C4A76B] border-[#C4A76B]/20',
    'moderada': 'bg-amber-900/20 text-amber-400 border-amber-500/20',
    'debil': 'bg-red-900/20 text-red-400 border-red-500/20'
  };
  return (
    <span className={`px-3 py-1 text-xs border ${config[strength] || config['moderada']}`} data-testid={`strength-badge-${strength}`}>
      {strength}
    </span>
  );
};

const ExplanationIcon = ({ icon }) => {
  if (icon === 'strong') return <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />;
  if (icon === 'reciprocity') return <Heart size={14} className="text-[#C4A76B] flex-shrink-0" />;
  if (icon === 'caution') return <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />;
  if (icon === 'neutral') return <Clock size={14} className="text-[#8A8A8A] flex-shrink-0" />;
  return <Target size={14} className="text-[#8A8A8A] flex-shrink-0" />;
};

const MatchExplanationCard = ({ match }) => {
  const [expanded, setExpanded] = useState(false);
  const exp = match.explanation;
  
  return (
    <div className="card-premium p-5 border border-[#222]" data-testid={`ai-match-${match.match_id}`}>
      <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#F5F0E6]">{match.user_a_name}</span>
            <Heart size={12} className="text-[#8B1A1A]" />
            <span className="text-[#F5F0E6]">{match.user_b_name}</span>
          </div>
          <StrengthBadge strength={exp.connection_strength} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl font-serif text-[#F5F0E6]">{exp.compatibility_score}</span>
          <ChevronRight size={16} className={`text-[#555] transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>
      
      {/* Quick explanations preview */}
      {!expanded && exp.explanations.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-[#8A8A8A]">
          <ExplanationIcon icon={exp.explanations[0].icon} />
          <span className="truncate">{exp.explanations[0].text}</span>
        </div>
      )}
      
      {/* Expanded detail */}
      {expanded && (
        <div className="mt-4 space-y-4 pt-4 border-t border-[#222]">
          {/* Score breakdown bar */}
          <div className="space-y-2">
            <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-3">Desglose de compatibilidad</p>
            {exp.score_breakdown.map((factor, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-[#8A8A8A] text-xs w-40 flex-shrink-0">{factor.factor}</span>
                <div className="flex-1 h-2 bg-[#222] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      factor.score >= 70 ? 'bg-emerald-400' : factor.score >= 50 ? 'bg-[#C4A76B]' : 'bg-red-400'
                    }`}
                    style={{ width: `${factor.score}%` }}
                  />
                </div>
                <span className="text-xs font-mono text-[#8A8A8A] w-10 text-right">{factor.score}</span>
              </div>
            ))}
          </div>
          
          {/* Explanations */}
          <div className="space-y-2">
            <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-3">Analisis IA</p>
            {exp.explanations.map((e, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2 bg-[#111] border border-[#1a1a1a]">
                <ExplanationIcon icon={e.icon} />
                <span className="text-sm text-[#F5F0E6]">{e.text}</span>
              </div>
            ))}
          </div>
          
          {/* Stagnation reasons */}
          {exp.stagnation_reasons.length > 0 && (
            <div className="space-y-2">
              <p className="text-amber-400 text-xs uppercase tracking-wider mb-3">Razones de estancamiento</p>
              {exp.stagnation_reasons.map((r, i) => (
                <div key={i} className="flex items-start gap-3 px-3 py-2 bg-amber-900/10 border border-amber-500/10">
                  <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-amber-300">{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* Event feedback summary */}
          {exp.event_feedback_summary && exp.event_feedback_summary.total_interactions > 0 && (
            <div className="flex items-center gap-4 text-xs text-[#8A8A8A]">
              <span>Feedback evento:</span>
              <span className="text-emerald-400">{exp.event_feedback_summary.positive} positivos</span>
              <span>{exp.event_feedback_summary.neutral} neutros</span>
              <span className="text-red-400">{exp.event_feedback_summary.negative} negativos</span>
            </div>
          )}

          {/* Match meta */}
          <div className="flex items-center gap-4 text-xs text-[#555] pt-2 border-t border-[#1a1a1a]">
            <span>Nivel: {match.level}</span>
            <span>Estado: {match.status}</span>
            {match.matched_at && (
              <span>{new Date(match.matched_at).toLocaleDateString('es-ES')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ExplainableAIDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics/explainable-ai`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#8B1A1A]" /></div>;
  if (!data) return <p className="text-[#8A8A8A]">Error cargando analytics</p>;

  const { aggregate, insights, match_explanations } = data;

  return (
    <div className="space-y-8" data-testid="explainable-ai-dashboard">
      <div>
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">IA Explicable</h2>
        <p className="text-[#8A8A8A]">
          Analisis profundo de por que las conexiones funcionan o no. {data.total_matches_analyzed} conexiones analizadas.
        </p>
      </div>

      {/* AI Insights */}
      {insights.length > 0 && (
        <div className="space-y-3">
          <p className="text-[#8A8A8A] text-xs uppercase tracking-wider">Insights de IA</p>
          {insights.map((insight, i) => (
            <div
              key={i}
              className={`p-4 border ${
                insight.type === 'positive' ? 'bg-emerald-900/10 border-emerald-500/15' :
                insight.type === 'concern' ? 'bg-red-900/10 border-red-500/15' :
                'bg-[#C4A76B]/5 border-[#C4A76B]/15'
              }`}
              data-testid={`ai-insight-${i}`}
            >
              <div className="flex items-start gap-3">
                {insight.type === 'positive' ? <CheckCircle size={16} className="text-emerald-400 mt-0.5" /> :
                 insight.type === 'concern' ? <AlertCircle size={16} className="text-red-400 mt-0.5" /> :
                 <Sparkles size={16} className="text-[#C4A76B] mt-0.5" />}
                <div>
                  <p className="text-[#F5F0E6] text-sm">{insight.text}</p>
                  <span className={`text-xs mt-1 inline-block ${
                    insight.priority === 'high' ? 'text-[#8B1A1A]' : 'text-[#555]'
                  }`}>
                    Prioridad {insight.priority === 'high' ? 'alta' : 'media'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Strength Distribution */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(aggregate.strength_distribution).map(([strength, count]) => (
          <div key={strength} className="card-premium p-5 text-center">
            <p className="text-3xl font-serif text-[#F5F0E6]">{count}</p>
            <StrengthBadge strength={strength} />
          </div>
        ))}
      </div>

      {/* Factor Averages */}
      {Object.keys(aggregate.factor_averages).length > 0 && (
        <div>
          <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-4">Promedios por factor de compatibilidad</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(aggregate.factor_averages).map(([key, val]) => {
              const labels = {
                intention: 'Intenciones',
                lifestyle: 'Estilo de vida',
                psychology: 'Psicologia',
                semantic: 'Valores',
                premium_alignment: 'Afinidad premium'
              };
              return (
                <div key={key} className="card-premium p-4">
                  <p className="text-[#F5F0E6] text-sm mb-2">{labels[key] || key}</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-3 bg-[#222] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${val.average_score >= 70 ? 'bg-emerald-400' : val.average_score >= 50 ? 'bg-[#C4A76B]' : 'bg-red-400'}`}
                        style={{ width: `${val.average_score}%` }}
                      />
                    </div>
                    <span className="text-sm font-mono text-[#F5F0E6]">{val.average_score}</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-[#555]">
                    <span>{val.high_count} altos</span>
                    <span>{val.low_count} bajos</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stagnation Reasons */}
      {aggregate.top_stagnation_reasons.length > 0 && (
        <div>
          <p className="text-amber-400 text-xs uppercase tracking-wider mb-4">Principales causas de estancamiento</p>
          <div className="space-y-2">
            {aggregate.top_stagnation_reasons.map((item, i) => (
              <div key={i} className="card-premium p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={14} className="text-amber-400" />
                  <span className="text-[#F5F0E6] text-sm">{item.reason}</span>
                </div>
                <span className="text-amber-400 font-mono text-sm">{item.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-match explanations */}
      <div>
        <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-4">Detalle por conexion</p>
        <div className="space-y-3">
          {match_explanations.map(m => (
            <MatchExplanationCard key={m.match_id} match={m} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
